/* Something failed. This says what, and what to do about it.
 *
 * Never "oops", never "something went wrong", never a cartoon. Those are all
 * the same sentence — "we know and we are not telling you" — and they leave a
 * person with no move except reloading and hoping.
 *
 * The distinction that earns its keep is `status === 0`: the request never
 * reached us. "You are on a train" and "our API is down" want different words,
 * and api.ts already keeps them apart, so this component only has to respect
 * the difference.
 *
 * Three redundant signals, the same as a form field: a 4px rule, a magenta
 * block on the left, and the words. Colour is never carrying this alone.
 */

import type { ReactNode } from 'react';

import { ApiError } from '../api';

export interface ErrorNoteProps {
  /** Whatever was caught. An ApiError gets a better sentence than a bare Error. */
  error?: unknown;
  /**
   * What was being done, as a noun phrase: "the schedule", "this episode".
   * Used to build the first line — "We could not load the schedule."
   */
  what?: string;
  /** Overrides the generated sentence entirely. */
  children?: ReactNode;
  /** Draws a retry button. Leave it out when there is nothing sensible to retry. */
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

/** The mono code in the corner: something to quote to us, never an excuse. */
function codeFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return 'NO CONNECTION';
    return `HTTP ${error.status}`;
  }
  return 'CLIENT ERROR';
}

function sentenceFor(error: unknown, what: string): string {
  if (error instanceof ApiError) {
    if (error.isOffline) {
      return `We could not reach the station to load ${what}. That is usually the connection at your end — the request never arrived here.`;
    }
    if (error.status === 404) {
      return `There is nothing at that address, so ${what} could not be loaded. Catalogue numbers get mistyped and bookings get moved; if you followed a link from this site, the mistake is ours.`;
    }
    if (error.status === 429) {
      return `That is more requests than we allow in a minute, so ${what} was not loaded. Wait a moment and try again.`;
    }
    if (error.isUnauthorised) {
      return `You are not signed in, so ${what} could not be loaded. Sign in and come back to this page.`;
    }
    if (error.status >= 500) {
      return `The station API failed while loading ${what}. That one is ours, not yours, and it is usually over in a minute.`;
    }
    return error.detail || `We could not load ${what}.`;
  }

  if (error instanceof Error && error.message) {
    return `We could not load ${what}. The browser said: ${error.message}`;
  }
  return `We could not load ${what}, and we did not get told why.`;
}

export function ErrorNote({
  error,
  what = 'this',
  children,
  onRetry,
  retryLabel = 'Try again',
  className,
}: ErrorNoteProps) {
  return (
    <div className={className ? `note ${className}` : 'note'} role="alert">
      <div className="note__blk" aria-hidden="true" />
      <div className="note__body">
        <div className="mono note__code">{codeFor(error)}</div>
        <p className="t-body note__text">{children ?? sentenceFor(error, what)}</p>
        {onRetry ? (
          <div className="note__acts">
            <button type="button" className="btn-ghost" onClick={onRetry}>
              {retryLabel}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
